<script setup lang="ts">
  import { Button, ButtonSize, ButtonType, Picture, PictureFormat, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import {
    getSerpNoticeLeadBannerContext,
    type LeadBannerTrackingContext,
    type LeadBannerTriggerReason,
  } from '@/entities/analytics';
  import { LeadBanner, checkLeadSession } from '@/features/leadBanner';
  import type { DepartureCity } from '@/shared/model';
  import { SERP_ALTERNATIVE_SEARCH_TEXTS } from '../config/texts';
  import { formatAlternativeSearchSummary } from '../lib/alternativeSearchSummary';
  import type { AlternativeSearchView } from '../model/alternativeSearch/types';

  type DepartureCityView = Extract<AlternativeSearchView, { kind: 'departureCity' }>;

  interface SerpAlternativeSearchNoticeProps {
    view: AlternativeSearchView;
    leadTriggerReason: LeadBannerTriggerReason;
  }

  const props = defineProps<SerpAlternativeSearchNoticeProps>();

  interface SerpAlternativeSearchNoticeEmits {
    (e: 'select-city', city: DepartureCity): void;
  }

  const emit = defineEmits<SerpAlternativeSearchNoticeEmits>();

  const isLeadBannerVisible = !checkLeadSession();

  const leadBannerTracking = computed<LeadBannerTrackingContext>(() =>
    getSerpNoticeLeadBannerContext(props.leadTriggerReason),
  );

  const departureCityView = computed<DepartureCityView | null>(() =>
    props.view.kind === 'departureCity' ? props.view : null,
  );

  const summary = computed<string | null>(() =>
    props.view.kind === 'similar' ? formatAlternativeSearchSummary(props.view.criteria) : null,
  );

  const isMessageNarrow = computed<boolean>(() => props.view.kind !== 'similar');

  const departureCityTitle = computed<string>(() =>
    departureCityView.value
      ? `${SERP_ALTERNATIVE_SEARCH_TEXTS.departureCityTitlePrefix}${departureCityView.value.city.name}`
      : '',
  );

  const message = computed<string>(() => {
    const { departureCityMessagePrefix, similarMessage, notFoundMessage } = SERP_ALTERNATIVE_SEARCH_TEXTS;

    if (departureCityView.value) return `${departureCityMessagePrefix}${departureCityView.value.userCity.name}`;

    return props.view.kind === 'similar' ? similarMessage : notFoundMessage;
  });

  const onSelectCity = (city: DepartureCity): void => {
    emit('select-city', city);
  };
</script>

<template>
  <section
    class="serp-alternative-search-notice"
    data-testid="serp-alternative-search-notice"
  >
    <div class="serp-alternative-search-notice__status">
      <Picture
        path="illustrations/map.webp"
        :extensions="[PictureFormat.WEBP]"
        :high-resolution="true"
        :lazy="false"
        class="serp-alternative-search-notice__picture"
        fit="contain"
        aria-hidden="true"
      />
      <Typo
        :class="[
          'serp-alternative-search-notice__message',
          { 'serp-alternative-search-notice__message_narrow': isMessageNarrow },
        ]"
        :variant="TypoVariant.BodyLRegular"
        tag="p"
        data-testid="serp-alternative-search-notice-message"
      >
        {{ message }}
      </Typo>
    </div>

    <LeadBanner
      v-if="isLeadBannerVisible"
      :title="SERP_ALTERNATIVE_SEARCH_TEXTS.leadTitle"
      :subtitle="SERP_ALTERNATIVE_SEARCH_TEXTS.leadSubtitle"
      :button-text="SERP_ALTERNATIVE_SEARCH_TEXTS.leadButton"
      :mobile="true"
      :tracking="leadBannerTracking"
      data-testid="serp-alternative-search-lead-banner"
    />

    <div
      v-if="departureCityView"
      class="serp-alternative-search-notice__recommendations"
    >
      <div class="serp-alternative-search-notice__heading">
        <Typo
          class="serp-alternative-search-notice__title"
          :variant="TypoVariant.Heading5"
          data-testid="serp-alternative-search-notice-title"
        >
          {{ departureCityTitle }}
        </Typo>
        <Typo
          class="serp-alternative-search-notice__text serp-alternative-search-notice__text_hint"
          :variant="TypoVariant.BodyLRegular"
          tag="p"
          data-testid="serp-alternative-search-notice-hint"
        >
          {{ SERP_ALTERNATIVE_SEARCH_TEXTS.departureCityHint }}
        </Typo>
      </div>

      <div
        v-if="departureCityView.cities.length"
        class="serp-alternative-search-notice__cities"
        data-testid="serp-alternative-search-notice-cities"
      >
        <Button
          v-for="city in departureCityView.cities"
          :key="city.id"
          :type="ButtonType.Link"
          :size="ButtonSize.M"
          :data-testid="`serp-alternative-city-${city.id}`"
          @click="onSelectCity(city)"
        >
          {{ city.name }}
        </Button>
      </div>
    </div>

    <div
      v-else-if="summary !== null"
      class="serp-alternative-search-notice__recommendations"
    >
      <div class="serp-alternative-search-notice__heading">
        <Typo
          class="serp-alternative-search-notice__title"
          :variant="TypoVariant.Heading5"
          data-testid="serp-alternative-search-notice-title"
        >
          {{ SERP_ALTERNATIVE_SEARCH_TEXTS.similarTitle }}
        </Typo>
        <Typo
          class="serp-alternative-search-notice__text"
          :variant="TypoVariant.BodyLRegular"
          tag="p"
          data-testid="serp-alternative-search-notice-summary"
        >
          {{ summary }}
        </Typo>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
  .serp-alternative-search-notice {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xl);
    padding: var(--spacing-m) var(--spacing-xs) 0;
    width: 100%;

    &__status {
      align-items: center;
      display: flex;
      flex-direction: column;
    }

    &__picture {
      height: 100px;
      width: 100px;
    }

    &__message {
      color: var(--color-text-primary);
      margin: 0;
      text-align: center;
      width: 100%;

      &_narrow {
        max-width: 255px;
      }
    }

    &__recommendations {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      padding-bottom: var(--spacing-xs);
    }

    &__heading {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxs);
    }

    &__title,
    &__text {
      color: var(--color-text-primary);
      margin: 0;
    }

    &__text_hint {
      max-width: 273px;
    }

    &__cities {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-xs) var(--spacing-m);
    }
  }
</style>
